import { useMediaQuery } from '@mantine/hooks';
import { domAnimation, LazyMotion, m, AnimatePresence } from 'motion/react';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ProductGallery } from './ProductGallery';
import {
  getPreviousSearchResults,
  getDesignatedCtas,
  startTextStreamFetch,
  consumeTextStream,
} from './services/chat-api.service';
import { consumeTextStreamEvents } from './services/text-stream-consumer';
import { SystemMessage } from './system-message/SystemMessage';
import { Providers } from './Providers';
import { DevTools } from './dev-tools/DevTools';
import { useGalleryVisibility } from './hooks/useGalleryVisibility';
import { useGetStoreInfo } from './hooks/useGetStoreInfo';
import { useWindowHandlers } from './hooks/useWindowhandlers';
import { useGetProductId } from './hooks/useGetProductId';
import {
  type DisplayFilterGroup,
  ResponseType,
  type UnifiedSearchApiResponse,
} from './services/types';
import { useAnalytics } from './hooks/useAnalytics';
import { useIdentifySession } from './hooks/useIdentifySession';
import { useChatBarVisibility } from './hooks/useChatBarVisibility';
import {
  ChatContextProvider,
  DisplayMode,
  SuperBarViewState,
  SystemMessageState,
  useChatContext,
} from './context/use-chat-context';
import { useLockScroll } from './hooks/useLockScroll';
import { useUserContext } from './context/use-user-context';
import { AnalyticsEvent } from './constants/analyticsEvents';
import { Featureflag } from './constants/featureFlags';
import { SuperbarMenu } from './SuperbarMenu';
import { cn } from './utils/styles';
import { stopRequestById } from './services/request-handler.service';
import {
  getSearchAiFilters,
  getAiFiltersForPage,
} from './services/ai-filters-api.service';
import { SuperbarInput } from './SuperbarInput';
import { useSuperbarPosition } from './hooks/useSuperbarPosition';
import { usePositionItemOnTopOfSuperbar } from './hooks/usePositionItemOnTopOfSuperbar';
import { useOverrideZIndexForTdotDropdown } from './hooks/useOverrideZIndexForTdotDropdown';
import { navigateTo } from './utils/url';
import { useSuperbarEventDispatcher } from './hooks/useSuperbarEventDispatcher';
import { useOnboardingInitialization } from './hooks/useOnboardingInitialization';
import { usePreviousSuperbarState } from './hooks/usePreviousSuperbarState';
import { useStoreCustomStyles } from './hooks/useStoreCustomStyles';
import { useSuperbarWindowApi } from '../api/useSuperbarWindowApi';
import {
  isAiFiltersEnabledInStore,
  buildEnabledFlags,
  shouldShowAiFilters as shouldShowAiFiltersHelper,
  createDisplayMessage,
} from './utils/chat-helpers';
import { useIsOnboarding } from './hooks/useIsOnboarding';
import { useGetProactiveEngagement } from './hooks/useGetProactiveEngament';
import { useExternalLinkClickListener } from './hooks/useExternalLinkClickListener';
import { useMoveCaddieRootToParent } from './hooks/useMoveCaddieRootToParent';
import { useSuperbarHover } from './hooks/useSuperbarHover';
import { useDelayedLoad } from './hooks/useDelayedLoad';
import { useHideOnScroll } from './hooks/useHideOnScroll';
import { useConversionTracking } from './hooks/useConversionTracking';
import { usePurchaseTracking } from './hooks/usePurchaseTracking';
import {
  useExitIntent,
  DEFAULT_EXIT_INTENT_QUESTION,
} from './hooks/useExitIntent';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { growthbook } from './Growthbook';
import localFeatures from './growthbook.local.json';
import { useSuperbarTheme } from './hooks/useSuperbarTheme';
import { useVoiceMode } from './hooks/useVoiceMode';
import { useSpeechToText } from './hooks/useSpeechToText';
import { useDictateAnalyser } from './hooks/useDictateAnalyser';
import { styleToColorMode } from './themes/theme';
import { getEnv } from '../utils/env';
import GlowBackground from './GlowBackground';
import {
  ActiveStateBackground,
  GLOW_COLOR_PALETTES,
} from './constants/glow-colors';
import { useAIAgentTracking } from './hooks/useAIAgentTracking';

const INITIAL_PAGE = 1;

const getDisplayModeFromResponse = ({
  response,
}: {
  response: UnifiedSearchApiResponse;
}): DisplayMode => {
  if (response.responseType === ResponseType.CATEGORY_ROUTE) {
    return DisplayMode.CATEGORY_ROUTE;
  }

  if (response.responseType === ResponseType.PRODUCT_SEARCH) {
    return response.filteredDocuments.length
      ? DisplayMode.SEARCH
      : DisplayMode.NO_RESULTS;
  }

  return DisplayMode.ASSISTANT;
};

interface FeedbackControlProps {
  userQuery: string;
  hasAnswer: boolean;
  showFeedbackAddedNudge: boolean;
  setShowFeedbackAddedNudge: (v: boolean) => void;
  setPendingAskAgainQuery: (q: string | null) => void;
  onClose: () => void;
  searchId: string | null;
  isPlayground: boolean;
  showSuggestionsInBanner: boolean;
  onSelectSuggestedQuestion: (q: string) => void;
}

interface SuggestedQuestionsViewProps {
  onSelect: (q: string) => void;
}

const ChatViewComponent = ({
  feedbackControl,
  suggestedQuestionsView,
  onPlaygroundProgress,
}: {
  feedbackControl?: (props: FeedbackControlProps) => ReactNode;
  suggestedQuestionsView?: (props: SuggestedQuestionsViewProps) => ReactNode;
  onPlaygroundProgress?: () => void;
}) => {
  const {
    displayMessage,
    searchResults,
    searchId,
    parentSearchId,
    urlSearchQuery,
    appliedFilterGroups,
    setDisplayMessage,
    setDisplayMode,
    setAppliedFilterGroups,
    setChatState,
    updateDisplayMessageAnswer,
    updateDisplayMessageMetadata,
    clearAll,
    currentPage,
    setSearchId,
    isGalleryLoading,
    setIsGalleryLoading,
    showError,
    setShowError,
    superBarViewState,
    setSuperBarViewState,
    systemMessageState,
    isLoadingAiFilters,
    setIsFetchingAiFilters,
    setAiFiltersGroupsDisplayed,
    setSystemMessageState,
    onboardingStep,
    setOnboardingStep,
    isSuperserveUi,
    isPlayground,
    pendingAskAgainQuery,
    setPendingAskAgainQuery,
    showFeedbackAddedNudge,
    setShowFeedbackAddedNudge,
    setDesignatedCtas,
    designatedCtas,
    setIsTextStreamActive,
  } = useChatContext();
  const theme = useSuperbarTheme();

  const [fullScreenTakeover, setFullScreenTakeover] = useState(false);

  const barRef = useRef<HTMLDivElement | null>(null);
  const {
    dispatchMessageSent,
    dispatchMessageReceived,
    dispatchSessionAvailable,
  } = useSuperbarEventDispatcher();

  const [value, setValue] = useState(urlSearchQuery || '');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isCompactView, setIsCompactView] = useState(true);

  // For width calculations, both EXPANDED and DISPLAYING_ANSWER use expanded width
  const isExpandedWidth =
    superBarViewState === SuperBarViewState.EXPANDED ||
    superBarViewState === SuperBarViewState.DISPLAYING_ANSWER;

  const previousAppliedSearchIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (pendingAskAgainQuery) {
      setValue(pendingAskAgainQuery);
      setPendingAskAgainQuery(null);
      setDisplayMessage(undefined);
    }
  }, [pendingAskAgainQuery, setPendingAskAgainQuery, setDisplayMessage]);

  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : '';

  const isFeedbackVisible = useMemo(() => {
    // Keep visible while nudge is showing (displayMessage may have been cleared)
    if (showFeedbackAddedNudge) return true;

    // Only show feedback when a question has been answered
    const hasAnswer = !!displayMessage?.systemAnswer?.length;

    // Show training banner on playground when superbar expands before any answer
    if (isPlayground && isExpandedWidth && !hasAnswer && !loading && !showError)
      return true;

    // Don't show during loading or errors
    if (loading || showError || !hasAnswer) {
      return false;
    }

    // For Superserve UI, always show feedback
    if (isSuperserveUi) return true;

    // Don't show for regular UI
    return false;
  }, [
    displayMessage?.systemAnswer,
    loading,
    showError,
    isSuperserveUi,
    showFeedbackAddedNudge,
    isPlayground,
    isExpandedWidth,
  ]);

  const suggestionsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const [suggestionsTimerFired, setSuggestionsTimerFired] = useState(false);

  useEffect(() => {
    const shouldRun =
      isPlayground && isExpandedWidth && !value && !displayMessage && !loading;

    if (!shouldRun) {
      if (suggestionsTimerRef.current) {
        clearTimeout(suggestionsTimerRef.current);
        suggestionsTimerRef.current = null;
      }
      setSuggestionsTimerFired(false);
      return;
    }

    suggestionsTimerRef.current = setTimeout(() => {
      setSuggestionsTimerFired(true);
    }, 5000);

    return () => {
      if (suggestionsTimerRef.current) {
        clearTimeout(suggestionsTimerRef.current);
      }
    };
  }, [isPlayground, isExpandedWidth, value, displayMessage, loading]);

  const showSuggestionsInBanner =
    suggestionsTimerFired && isPlayground && !displayMessage && !loading;

  const isMobile = useMediaQuery('(max-width: 767px)');
  const isMd = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
  const isLg = useMediaQuery('(min-width: 1024px)');
  useWindowHandlers();

  useExternalLinkClickListener({
    rootId: 'caddie-root',
    listener: () => {
      handleClearAllAIFilters();
    },
  });

  const { currentSession, store, storeSettings } = useGetStoreInfo();

  const storeConfig = useMemo(
    () => store?.config?.config,
    [store?.config?.config]
  );
  const productId = useGetProductId(storeConfig);

  useEffect(() => {
    const storeId = store?.id;
    if (storeId == null) return;
    growthbook.setAttributes({
      ...growthbook.getAttributes(),
      storeId,
    });
  }, [store?.id]);

  useIdentifySession({
    session: currentSession,
    store,
  });

  // Track AI agent/crawler visits
  useAIAgentTracking();

  const engagementConfig = storeConfig?.engagementConfig;

  const { trackEvent } = useAnalytics();

  const glowColors = useMemo(() => {
    const setting =
      (storeSettings?.settings
        ?.activeStateBackground as ActiveStateBackground) ||
      ActiveStateBackground.None;
    return GLOW_COLOR_PALETTES[setting];
  }, [storeSettings?.settings?.activeStateBackground]);

  const showGlow = useMemo(() => {
    return (
      ((storeSettings?.settings
        ?.activeStateBackground as ActiveStateBackground) ||
        ActiveStateBackground.None) !== ActiveStateBackground.None
    );
  }, [storeSettings?.settings?.activeStateBackground]);

  // Ref bridges: voice mode needs sendChatMessage and stopAllAudio, but those
  // depend on hooks declared earlier. handleSpeechEndRef routes STT results to
  // voice mode's orchestrator. All three are assigned after their respective
  // hook calls complete (see lines ~676-718).
  // Order: (1) declare refs -> (2) call hooks -> (3) assign latest values each render
  const sendChatMessageRef = useRef<
    (args: {
      newMessage?: string;
      filterGroups?: DisplayFilterGroup[];
    }) => Promise<{ answer?: string } | undefined>
  >(() => Promise.resolve(undefined));
  const stopAllAudioRef = useRef<() => void>(() => {});

  const hasDesignatedCtas = designatedCtas.length > 0;

  const { onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave } =
    useSuperbarHover({
      autoExpandEnabled: false, // Commented out for now
      // autoExpandDelaySeconds: engagementConfig?.autoExpandDelaySeconds,
      onAutoExpand: () => setSuperBarViewState(SuperBarViewState.DEFAULT),
    });

  const { isVisible: isDelayedLoadComplete } = useDelayedLoad({
    delaySeconds: storeConfig
      ? (engagementConfig?.delayedLoadSeconds ?? 0)
      : undefined,
  });

  const exitIntent = useExitIntent({
    enabled: !isPlayground && !!engagementConfig?.exitIntentEnabled,
    question: engagementConfig?.exitIntentQuestion,
    onExitIntent: () => {
      setSuperBarViewState(SuperBarViewState.DEFAULT);
      setSystemMessageState(SystemMessageState.EXIT_INTENT);
      setDisplayMessage({
        systemAnswer:
          engagementConfig?.exitIntentQuestion || DEFAULT_EXIT_INTENT_QUESTION,
      });
      setFullScreenTakeover(true);
    },
  });

  const { onDismiss: onExitIntentDismiss } = exitIntent;
  const handleDismissTakeover = useCallback(() => {
    onExitIntentDismiss();
    setSystemMessageState(SystemMessageState.HIDDEN);
    setDisplayMessage(undefined);
    setFullScreenTakeover(false);
  }, [setSystemMessageState, setDisplayMessage, onExitIntentDismiss]);

  const handleFeedbackClose = useCallback(() => {
    setShowError(false);
    setSystemMessageState(SystemMessageState.HIDDEN);
    setDisplayMode(DisplayMode.ASSISTANT);
    setDisplayMessage(undefined);
  }, [setShowError, setSystemMessageState, setDisplayMode, setDisplayMessage]);

  useHideOnScroll({
    enabled: !isPlayground && !!engagementConfig?.hideOnScrollEnabled,
  });

  const { isFeatureEnabled, isLoadingFlags, aiFiltersOverride } =
    useUserContext();
  const aiFiltersEnabled =
    aiFiltersOverride ?? isFeatureEnabled(Featureflag.AI_FILTERS);
  const textStreamAbortRef = useRef<AbortController | null>(null);

  const { isVisible: isAvailableToCurrentUser } = useChatBarVisibility({
    store,
  });

  useConversionTracking({
    urls: engagementConfig?.conversionUrls,
    superbarVisible: isAvailableToCurrentUser,
    isLoadingFlags,
  });

  usePurchaseTracking({
    enabled: !isPlayground,
    platformType: storeConfig?.platformType,
    purchaseAutoDetectEnabled: engagementConfig?.purchaseAutoDetectEnabled,
    superbarVisible: isAvailableToCurrentUser,
    isLoadingFlags,
  });

  const { loading: customStylesLoading } = useStoreCustomStyles({
    enabled: isAvailableToCurrentUser,
    storeId: currentSession?.store?.id,
  });

  const isShowingProducts: boolean = useMemo(() => {
    return !!searchResults?.products?.length;
  }, [searchResults?.products]);

  const isOnboarding = useIsOnboarding({
    store,
    currentSession,
    onboardingStep,
    searchId,
    urlSearchQuery,
  });

  useSuperbarWindowApi();

  useGalleryVisibility({
    isShowingProducts,
  });

  useLockScroll({
    isShowingProducts,
  });

  useGetProactiveEngagement({
    productId,
    session: currentSession,
    onGetProactiveEngagement: (proactiveEngagement) => {
      if (displayMessage || loading || showError) return;

      // Don't show proactive engagement if user is in onboarding mode
      if (isOnboarding) {
        return;
      }

      setDisplayMessage({
        userQuery: proactiveEngagement.userQuery,
        systemAnswer: proactiveEngagement.systemAnswer,
      });
      setShowError(false);
    },
    store,
    enabled:
      isAvailableToCurrentUser &&
      !!productId &&
      !displayMessage &&
      !loading &&
      !showError &&
      !isOnboarding,
  });

  // Use custom hook for onboarding initialization
  useOnboardingInitialization({
    currentSession,
    store,
    onboardingStep,
    searchId,
    urlSearchQuery,
    setDisplayMessage,
    setDisplayMode,
    setSuperBarViewState,
    setOnboardingStep,
    eventDispatcher: {
      dispatchMessageSent,
      dispatchMessageReceived,
      dispatchSessionAvailable,
    },
  });

  useMoveCaddieRootToParent({
    storeConfig,
  });

  const isAiFiltersEnabledInStoreConfig = isAiFiltersEnabledInStore(store);
  const isAIFiltersEnabled =
    (isFeatureEnabled(Featureflag.AI_FILTERS) &&
      isAiFiltersEnabledInStoreConfig) ||
    aiFiltersEnabled;

  const isCollectionsEnabled = isFeatureEnabled(Featureflag.COLLECTIONS);

  const enabledFlags = useMemo(
    () =>
      buildEnabledFlags({
        isAIFiltersEnabled,
        isCollectionsEnabled,
      }),
    [isAIFiltersEnabled, isCollectionsEnabled]
  );

  const shouldShowAIFilters = useMemo(
    () =>
      shouldShowAiFiltersHelper({
        isAIFiltersEnabled: isAIFiltersEnabled,
        searchId,
        productsCount: searchResults?.products?.length,
        appliedFiltersCount: appliedFilterGroups?.length ?? 0,
      }),
    [
      isAIFiltersEnabled,
      searchId,
      searchResults?.products?.length,
      appliedFilterGroups?.length,
    ]
  );
  const hasNoSearchId = !searchId;

  const shouldShowAiFiltersForPage = isAIFiltersEnabled && hasNoSearchId;

  const usedFilters = useMemo(() => {
    return Array.from(
      new Set(
        appliedFilterGroups.flatMap((f) =>
          f.filters.map((filter) => filter.value)
        )
      )
    ).join('|');
  }, [appliedFilterGroups]);

  const prevSuperBarViewState = usePreviousSuperbarState(superBarViewState);

  const onSearchResults = useCallback(
    ({
      response,
      newPage,
      newMessage,
    }: {
      response: UnifiedSearchApiResponse;
      newPage: number;
      newMessage?: string;
    }) => {
      if (response.redirectUrl) {
        navigateTo(response.redirectUrl);
        return;
      }

      const { type: ctaType, actionProps } =
        response.suggestedCTA?.defaultAction ?? {};
      if (ctaType === 'navigate' && actionProps?.href) {
        navigateTo(actionProps.href);
        return;
      }

      const determinedDisplayMode = getDisplayModeFromResponse({
        response,
      });

      const messageToDisplay = createDisplayMessage({
        response,
        isAIFiltersEnabled,
      });

      if (
        determinedDisplayMode === DisplayMode.SEARCH ||
        determinedDisplayMode === DisplayMode.CATEGORY_ROUTE
      ) {
        const appliedFilterGroupsToSet = response.appliedFilters?.map(
          (filter) => ({
            label: filter.label,
            value: filter.value,
            type: filter.type,
            filters: [filter],
          })
        );

        const searchResultsToSet = {
          id: response.id as string,
          products: response.filteredDocuments,
          searchPagination: {
            currentPage: newPage || currentPage || 1,
            totalPages: response.totalPages,
            count: response.count,
          },
        };

        setChatState({
          displayMessage: messageToDisplay,
          appliedFilterGroups: appliedFilterGroupsToSet,
          searchResults: searchResultsToSet,
          displayMode: determinedDisplayMode,
          currentPage: newPage || currentPage || 1,
          searchId: searchResultsToSet.id,
          onboardingStep: response.onboardingStep,
          ...{
            ...(parentSearchId
              ? {}
              : {
                  parentSearchId:
                    response.parentSearchId || searchResultsToSet.id,
                }),
          },
        });
      } else {
        setChatState({
          displayMessage: messageToDisplay,
          displayMode: determinedDisplayMode,
          onboardingStep: response.onboardingStep,
        });
      }

      setDisplayMode(determinedDisplayMode);
      if (determinedDisplayMode === DisplayMode.NO_RESULTS) {
        trackEvent(AnalyticsEvent.NO_SEARCH_RESULTS_FOUND, {
          message: newMessage || displayMessage?.userQuery || '',
          sessionId: currentSession?.id,
          productId,
        });
      }

      setShowError(false);
      // Don't collapse the superbar while voice mode is handling the response
      // Use ASSISTANT state to keep expanded width when displaying answers
      if (voiceModeStateRef.current === 'off') {
        setSuperBarViewState(SuperBarViewState.DISPLAYING_ANSWER);
      }
    },
    [
      parentSearchId,
      isAIFiltersEnabled,
      setDisplayMode,
      currentPage,
      setChatState,
      setShowError,
      setSuperBarViewState,
      trackEvent,
      displayMessage?.userQuery,
      currentSession?.id,
      productId,
    ]
  );

  const sendChatMessage = useCallback(
    async ({
      newMessage = '',
      newPage = INITIAL_PAGE,
      filterGroups = [],
    }: {
      newMessage?: string;
      newPage?: number;
      filterGroups?: DisplayFilterGroup[];
    }) => {
      if (!store) return;
      stopAllAudioRef.current();
      setShowFeedbackAddedNudge(false);
      trackEvent(AnalyticsEvent.CHAT_MESSAGE_SENT, {
        message: newMessage,
        sessionId: currentSession?.id,
        productId,
      });

      if (isPlayground && newPage === INITIAL_PAGE) {
        onPlaygroundProgress?.();
      }

      dispatchMessageSent({
        message: newMessage,
        sessionId: currentSession?.id,
      });

      setLoading(true);
      setShowError(false);

      trackEvent(AnalyticsEvent.CHAT_MESSAGE_PROCESSING, {
        message: newMessage,
      });

      textStreamAbortRef.current?.abort();
      const abortController = new AbortController();
      textStreamAbortRef.current = abortController;

      setChatState({
        displayMessage: { userQuery: newMessage },
      });

      let receivedFirstChunk = false;
      let receivedResponse = false;

      try {
        setSuperBarViewState(SuperBarViewState.DISPLAYING_ANSWER);
        const response = await startTextStreamFetch({
          question: newMessage || '',
          productId,
          session: currentSession,
          store,
          signal: abortController.signal,
          filterGroups,
          enabledFlags,
          parentSearchId:
            parentSearchId && parentSearchId.length
              ? parentSearchId
              : undefined,
          page: newPage,
          pageSize: 24,
        });

        const stream = consumeTextStream(response);

        setIsTextStreamActive(true);
        await consumeTextStreamEvents(stream, {
          onTextChunk: (accumulated) => {
            if (!receivedFirstChunk) {
              receivedFirstChunk = true;
              setLoading(false);
              setChatState({ displayMode: DisplayMode.ASSISTANT });
            }
            updateDisplayMessageAnswer(accumulated);
          },
          onMetadata: (meta) => {
            updateDisplayMessageMetadata(meta);
          },
          onResponse: (data) => {
            receivedResponse = true;

            if (data.responseType === ResponseType.CATEGORY_ROUTE) {
              window.location.href = `${window.location.origin}${data.suggestedURL}`;
              return;
            }

            dispatchMessageReceived({
              message: newMessage,
              sessionId: currentSession?.id,
              onboardingStep: data.onboardingStep,
            });
            onSearchResults({ response: data, newPage, newMessage });
            setLoading(false);
          },
        });

        setIsTextStreamActive(false);
        // Stream completed - finalize for ASSISTANT path (text chunks)
        if (receivedFirstChunk && !receivedResponse) {
          dispatchMessageReceived({
            message: newMessage,
            sessionId: currentSession?.id,
          });
        }
        setValue('');
        setLoading(false);
        return undefined;
      } catch (error: unknown) {
        if (error instanceof Error && error.name === 'AbortError') {
          return undefined;
        }
        console.error('Error streaming message:', error);
        trackEvent(AnalyticsEvent.CHAT_MESSAGE_ERROR, {
          reason: error instanceof Error ? error.message : 'Unknown error',
          error,
        });
        setShowError(true);
        setIsTextStreamActive(false);
        setLoading(false);
        setSuperBarViewState(SuperBarViewState.DISPLAYING_ANSWER);
        return undefined;
      }
    },
    [
      currentSession,
      enabledFlags,
      onSearchResults,
      productId,
      setChatState,
      setShowError,
      setShowFeedbackAddedNudge,
      trackEvent,
      dispatchMessageSent,
      dispatchMessageReceived,
      updateDisplayMessageAnswer,
      updateDisplayMessageMetadata,
      parentSearchId,
      store,
      isPlayground,
      onPlaygroundProgress,
      setSuperBarViewState,
      setIsTextStreamActive,
    ]
  );

  sendChatMessageRef.current = sendChatMessage;

  // Single speech-to-text instance shared by voice input and voice mode.
  // onEnd routes to the voice mode orchestrator when active; otherwise
  // the transcript stays in the input for the user to submit manually.
  const handleSpeechEndRef = useRef<(t: string) => void>(() => {});
  const handleSpeculativeSilenceRef = useRef<(t: string) => void>(() => {});
  const handleSpeechResumedRef = useRef<() => void>(() => {});
  const stt = useSpeechToText({
    onEnd: (transcript: string) => handleSpeechEndRef.current(transcript),
    onSpeculativeSilence: (transcript: string) =>
      handleSpeculativeSilenceRef.current(transcript),
    onSpeechResumed: () => handleSpeechResumedRef.current(),
    onEvent: trackEvent,
  });

  const {
    voiceModeState,
    isTtsMuted,
    toggleVoiceMode,
    toggleTtsMute,
    exitVoiceMode,
    stopAllAudio,
    handleSpeechEnd,
    handleSpeculativeSilence,
    handleSpeechResumed,
    isVoiceModeAvailable: isVoiceModeEnabled,
    analyserService,
    isReadingOut,
  } = useVoiceMode({
    sendChatMessage: (args) => sendChatMessageRef.current(args),
    displayVoiceExchange: (transcript, answer) => {
      setLoading(true);
      setValue('');
      setChatState({
        displayMessage: {
          userQuery: transcript,
          systemAnswer: answer,
        },
        displayMode: DisplayMode.ASSISTANT,
      });
    },
    updateVoiceAnswer: (answer) => {
      setLoading(false);
      updateDisplayMessageAnswer(answer);
    },
    updateVoiceMetadata: updateDisplayMessageMetadata,
    appliedFilterGroups,
    startListening: stt.startListening,
    stopListening: stt.stopListening,
    trackEvent,
  });

  handleSpeechEndRef.current = handleSpeechEnd;
  handleSpeculativeSilenceRef.current = handleSpeculativeSilence;
  handleSpeechResumedRef.current = handleSpeechResumed;
  const voiceModeStateRef = useRef(voiceModeState);
  voiceModeStateRef.current = voiceModeState;

  useEffect(() => {
    if (voiceModeState === 'off') {
      setLoading(false);
    }
  }, [voiceModeState]);

  const isVoiceModeAvailable = stt.isSupported && isVoiceModeEnabled;

  const isDictating = stt.isListening && voiceModeState === 'off';
  const dictateAnalyserService = useDictateAnalyser(isDictating);
  const effectiveAnalyserService = isDictating
    ? dictateAnalyserService
    : analyserService;

  stopAllAudioRef.current = stopAllAudio;

  const moveToPage = useCallback(
    (page: number) => {
      if (!displayMessage) return;
      setIsGalleryLoading(true);

      sendChatMessage({
        newPage: page,
        newMessage: displayMessage.userQuery,
        filterGroups: appliedFilterGroups || [],
      }).finally(() => {
        setIsGalleryLoading(false);
      });
    },
    [displayMessage, setIsGalleryLoading, sendChatMessage, appliedFilterGroups]
  );

  const searchWithFilters = useCallback(
    (updatedFilterGroups: DisplayFilterGroup[]) => {
      if (!displayMessage) return;

      setIsGalleryLoading(true);

      trackEvent(AnalyticsEvent.AI_FILTER_SELECTED, {
        appliedFilters: updatedFilterGroups.map((f) => f.value).join(', '),
        sessionId: currentSession?.id,
        productId,
        storeId: currentSession?.store.id,
        storeUrl: currentSession?.store.url,
      });

      sendChatMessage({
        newMessage: displayMessage.userQuery,
        filterGroups: updatedFilterGroups,
      })
        .then(() => {
          setAppliedFilterGroups(updatedFilterGroups);
        })
        .finally(() => {
          setIsGalleryLoading(false);
        });
    },
    [
      displayMessage,
      setAppliedFilterGroups,
      sendChatMessage,
      setIsGalleryLoading,
      currentSession,
      productId,
      trackEvent,
    ]
  );

  const handleClearAllAIFilters = useCallback(() => {
    clearAll();
    setIsGalleryLoading(false);
    setLoading(false);
    setShowError(false);
  }, [clearAll, setIsGalleryLoading, setShowError]);

  const handleAIFilterRemove = useCallback(
    (filterGroup: DisplayFilterGroup) => {
      const updatedFilterGroups = appliedFilterGroups.filter(
        (f) => f.value !== filterGroup.value
      );
      setAppliedFilterGroups(updatedFilterGroups);
      searchWithFilters(updatedFilterGroups);
    },
    [appliedFilterGroups, setAppliedFilterGroups, searchWithFilters]
  );

  useEffect(() => {
    if (!pathname) return;
    if (pathname === '/') return;

    // Wait for initial load to complete to avoid race conditions with onboarding
    if (isInitialLoad) {
      return;
    }

    // Must have session to make API calls
    if (!currentSession?.id) {
      return;
    }

    // Don't fetch AI filters if user is in onboarding mode to avoid race conditions
    if (isOnboarding) {
      return;
    }

    // Fetch AI filters for the current page route
    const fetchAiFiltersForPage = async () => {
      setIsFetchingAiFilters(true);

      try {
        const response = await getAiFiltersForPage({
          session: currentSession,
          currentPath: pathname,
        });

        setAiFiltersGroupsDisplayed(response.filterGroupsToShow);
        setAppliedFilterGroups(response.appliedFilters || []);
        setDisplayMessage({
          userQuery: response.userQuery || '',
          systemAnswer: response.question,
        });

        trackEvent(AnalyticsEvent.AI_FILTERS_SHOWN, {
          question: response.question,
          appliedFilters: response.filterGroupsToShow
            .map((group) => group.value)
            .join(', '),
          sessionId: currentSession?.id,
          productId,
          storeId: currentSession?.store.id,
          storeUrl: currentSession?.store.url,
          categoryPath: pathname,
        });
        setSystemMessageState(SystemMessageState.AI_FILTERS);
      } catch (error) {
        console.error('Error fetching AI filters:', error);
      } finally {
        setIsFetchingAiFilters(false);
      }
    };

    if (!shouldShowAiFiltersForPage) return;

    fetchAiFiltersForPage().catch((error) => {
      console.error('Error fetching AI filters:', error);
    });
  }, [
    pathname,
    currentSession,
    productId,
    setAiFiltersGroupsDisplayed,
    setDisplayMessage,
    setIsFetchingAiFilters,
    trackEvent,
    shouldShowAiFiltersForPage,
    setAppliedFilterGroups,
    setSystemMessageState,
    isInitialLoad,
    isOnboarding,
  ]);

  useEffect(() => {
    const fetchPreviousSearchResults = async () => {
      if (!store) return;
      setLoading(true);
      setIsGalleryLoading(true);
      setShowError(false);

      trackEvent(AnalyticsEvent.RETURN_TO_SEARCH, {
        productId,
      });

      try {
        const previousSearchResults = await getPreviousSearchResults({
          searchId: searchId!,
          session: currentSession,
          store,
        });

        onSearchResults({
          response: previousSearchResults,
          newPage: previousSearchResults.currentPage,
          newMessage: previousSearchResults.query,
        });

        if (previousSearchResults.redirectUrl) {
          return;
        }
      } catch (error: any) {
        console.error('Error fetching previous search results:', error);

        trackEvent(AnalyticsEvent.CHAT_MESSAGE_ERROR, {
          reason: error.message,
          error: error,
        });

        setShowError(false);
        setSearchId(null);
        setSearchId(null);
        setAppliedFilterGroups([]);
      } finally {
        setLoading(false);
        setIsGalleryLoading(false);
        setIsInitialLoad(false);
      }
    };
    const currentSessionId = currentSession?.id;

    if (isLoadingFlags) return;
    if (!currentSessionId) return;
    if (searchResults?.products?.length) return;
    if (!isInitialLoad) return;

    if (urlSearchQuery) {
      sendChatMessage({
        newMessage: urlSearchQuery,
        filterGroups: appliedFilterGroups || [],
      }).finally(() => {
        setIsInitialLoad(false);
        setIsGalleryLoading(false);
      });
      return;
    }

    if (searchId) {
      fetchPreviousSearchResults().finally(() => {
        setIsInitialLoad(false);
        setIsGalleryLoading(false);
      });
      return;
    }
  }, [
    searchId,
    currentSession,
    isLoadingFlags,
    urlSearchQuery,
    productId,
    appliedFilterGroups,
    sendChatMessage,
    setIsGalleryLoading,
    setShowError,
    setAppliedFilterGroups,
    setSearchId,
    onSearchResults,
    isInitialLoad,
    searchResults?.products?.length,
    trackEvent,
    store,
  ]);

  useEffect(() => {
    if (!loading && isAvailableToCurrentUser) {
      trackEvent(AnalyticsEvent.SUPERBAR_DISPLAYED);
    }
  }, [loading, isAvailableToCurrentUser, trackEvent]);

  useEffect(() => {
    if (superBarViewState === SuperBarViewState.MINIMIZED && isMobile) {
      setSuperBarViewState(SuperBarViewState.DEFAULT);
    }
  }, [superBarViewState, setSuperBarViewState, isMobile]);

  useEffect(() => {
    const fetchAiFilters = async () => {
      if (!searchId) return;

      if (!currentSession?.id) {
        return;
      }

      setIsFetchingAiFilters(true);

      try {
        const response = await getSearchAiFilters({
          searchId,
          usedFilters,
          session: currentSession,
        });
        setAiFiltersGroupsDisplayed(response.filterGroupsToShow);
        setDisplayMessage({
          userQuery: response.searchResult.query,
          systemAnswer:
            searchResults?.products?.length &&
            searchResults?.products?.length >= 8
              ? response.question
              : '',
        });

        trackEvent(AnalyticsEvent.AI_FILTERS_SHOWN, {
          searchId,
          question: response.question,
          appliedFilters: response.filterGroupsToShow
            .map((group) => group.value)
            .join(', '),
          sessionId: currentSession?.id,
          productId,
          storeId: currentSession?.store.id,
          storeUrl: currentSession?.store.url,
        });
      } catch (error) {
        console.error('Error fetching AI filters:', error);
      } finally {
        setIsFetchingAiFilters(false);
      }
    };

    if (!searchId || !shouldShowAIFilters) return;

    const hasSearchIdChanged = previousAppliedSearchIdRef.current !== searchId;

    if (hasSearchIdChanged) {
      setAiFiltersGroupsDisplayed([]);
    }

    previousAppliedSearchIdRef.current = searchId;

    fetchAiFilters().catch((error) => {
      console.error('Error fetching AI filters:', error);
    });
  }, [
    usedFilters,
    productId,
    trackEvent,
    currentSession,
    searchId,
    searchResults?.products?.length,
    setAiFiltersGroupsDisplayed,
    setDisplayMessage,
    setIsFetchingAiFilters,
    shouldShowAIFilters,
  ]);

  useEffect(() => {
    if (!shouldShowAIFilters || !aiFiltersEnabled) {
      stopRequestById('getSearchAiFilters');
    }

    return () => {
      stopRequestById('getSearchAiFilters');
    };
  }, [shouldShowAIFilters, aiFiltersEnabled]);

  useEffect(() => {
    if (superBarViewState === SuperBarViewState.CLOSED) return;
    if (isLoadingFlags) return;

    const hasSearchId = Boolean(searchId);

    if (hasSearchId) {
      if (!isAIFiltersEnabled) {
        setIsCompactView(true);
        setSuperBarViewState(SuperBarViewState.DEFAULT);
        return;
      }

      setIsCompactView(false);
      setSuperBarViewState(SuperBarViewState.DEFAULT);
      return;
    }

    setIsCompactView(false);
  }, [
    isAIFiltersEnabled,
    isLoadingFlags,
    searchId,
    setSuperBarViewState,
    superBarViewState,
  ]);

  useSuperbarPosition({
    isAvailableToCurrentUser: isAvailableToCurrentUser,
    storeId: currentSession?.store?.id,
  });

  usePositionItemOnTopOfSuperbar({
    isAvailableToCurrentUser: isAvailableToCurrentUser,
    storeConfig,
  });

  useOverrideZIndexForTdotDropdown({
    storeId: currentSession?.store?.id,
    isAvailableToCurrentUser: isAvailableToCurrentUser,
  });

  useEffect(() => {
    if (currentSession?.id) {
      dispatchSessionAvailable();
    }
  }, [currentSession?.id, dispatchSessionAvailable]);

  useEffect(() => {
    const fetchDesignatedCtas = async () => {
      if (!store?.id) return;
      try {
        const ctas = await getDesignatedCtas(store.id);
        setDesignatedCtas(ctas);
      } catch (error) {
        console.error('Error fetching designated CTAs:', error);
      }
    };

    fetchDesignatedCtas();
  }, [store?.id, setDesignatedCtas]);

  useEffect(() => {
    setChatState({
      mode: styleToColorMode(storeSettings?.settings?.style),
    });
  }, [storeSettings?.settings?.style, setChatState]);

  if (
    !isAvailableToCurrentUser ||
    customStylesLoading ||
    isLoadingFlags ||
    !isDelayedLoadComplete
  ) {
    return null;
  }

  return (
    <>
      {isExpandedWidth && showGlow && (
        <div
          className={cn(
            'superbar-active-state-bg',
            'caddietw-absolute caddietw-bottom-0 caddietw-z-[-1]'
          )}
        >
          <GlowBackground
            expanded={isExpandedWidth}
            clipToRadius={false}
            radius={parseInt(theme.components.chatShellClosed.radius || '20')}
            opacity={1}
            blur={524}
            backdropBlur={12}
            leftColor={glowColors.leftColor}
            rightColor={glowColors.rightColor}
            poweredByColor={glowColors.poweredByColor}
            logoColor={glowColors.logoColor}
            state={isReadingOut ? 'speaking' : 'idle'}
            analyserService={effectiveAnalyserService}
          />
        </div>
      )}
      {/* Exit intent backdrop - glass only, no white fill */}
      {fullScreenTakeover && (
        <div
          className="caddietw-fixed caddietw-inset-0 caddietw-backdrop-blur-lg caddietw-z-[99998] caddietw-pointer-events-auto"
          style={{ pointerEvents: 'auto' }}
          onClick={handleDismissTakeover}
        />
      )}
      <LazyMotion features={domAnimation}>
        <AnimatePresence mode="wait">
          {[
            SuperBarViewState.DEFAULT,
            SuperBarViewState.MINIMIZED,
            SuperBarViewState.EXPANDED,
            SuperBarViewState.DISPLAYING_ANSWER,
          ].includes(superBarViewState) ? (
            <m.div
              ref={barRef}
              id={`chat-view-${superBarViewState}`}
              className={cn(
                'caddie-style-root caddietw-flex caddietw-flex-col caddietw-bg-transparent caddietw-z-[99999] caddietw-pointer-events-auto caddietw-overflow-visible',
                theme.className,
                theme.components.chatShell.className,
                superBarViewState === SuperBarViewState.DEFAULT &&
                  prevSuperBarViewState === SuperBarViewState.MINIMIZED &&
                  !isCompactView &&
                  'caddietw-w-[100%] md:caddietw-w-[70%] lg:caddietw-w-[40%] caddietw-relative', // AFTER expanding from minimized
                superBarViewState === SuperBarViewState.MINIMIZED &&
                  'caddietw-fixed caddietw-w-[40%] lg:caddietw-w-[30%] caddietw-bottom-0 caddietw-transform caddietw--translate-x-1/2 caddietw-z-50'
              )}
              onClick={(e) => e.stopPropagation()}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              initial={
                fullScreenTakeover
                  ? {
                      opacity: 0,
                      top: '120%',
                      left: '50%',
                      x: '-50%',
                      translateY: '-50%',
                      position: 'fixed' as const,
                    }
                  : { opacity: 0, y: 20 }
              }
              animate={
                fullScreenTakeover
                  ? {
                      opacity: 1,
                      top: '50%',
                      left: '50%',
                      x: '-50%',
                      translateY: '-50%',
                      position: 'fixed' as const,
                      width: isLg ? '760px' : isMd ? '600px' : '90%',
                    }
                  : isExpandedWidth
                    ? {
                        opacity: 1,
                        y: 0,
                        width: isLg ? '40%' : isMd ? '70%' : '100%',
                      }
                    : superBarViewState === SuperBarViewState.DEFAULT &&
                        !isCompactView
                      ? {
                          opacity: 1,
                          y: 0,
                          width: isLg ? '400px' : isMd ? '70%' : '100%',
                        }
                      : { opacity: 1, y: '100%' }
              }
              exit={
                fullScreenTakeover
                  ? {
                      opacity: 0,
                      top: '120%',
                      transition: { duration: 0.5, ease: [0.36, 0, 0.66, 0] },
                    }
                  : { opacity: 0, y: 20 }
              }
              transition={
                fullScreenTakeover
                  ? { duration: 0.5, ease: [0.16, 1, 0.3, 1] }
                  : {
                      duration: 0.3,
                      ease: [0.22, 1, 0.36, 1],
                      opacity: { duration: 0.2 },
                      y: { duration: 0.3 },
                      width: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
                      minWidth: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
                      layout: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
                    }
              }
              key={fullScreenTakeover ? 'takeover' : 'superbar-main'}
            >
              {isFeedbackVisible && (
                <m.div
                  className={cn(
                    'caddietw-absolute',
                    'caddietw-left-1/2',
                    'caddietw-transform',
                    'caddietw--translate-x-1/2',
                    'caddietw-bottom-full',
                    'caddietw-w-full',
                    'caddietw-max-w-full',
                    'md:caddietw-max-w-[70%]',
                    'lg:caddietw-max-w-[40%]',
                    'caddietw-flex',
                    'caddietw-justify-center'
                  )}
                  style={{ marginBottom: '-28px' }}
                  initial={{ opacity: 0, y: 10, x: '-50%' }}
                  animate={{ opacity: 1, y: 0, x: '-50%' }}
                  transition={{ delay: 0.5, duration: 0.3 }}
                >
                  {feedbackControl?.({
                    userQuery: displayMessage?.userQuery ?? '',
                    hasAnswer: !!displayMessage?.systemAnswer?.length,
                    showFeedbackAddedNudge,
                    setShowFeedbackAddedNudge,
                    setPendingAskAgainQuery,
                    onClose: handleFeedbackClose,
                    searchId,
                    isPlayground,
                    showSuggestionsInBanner,
                    onSelectSuggestedQuestion: (q) => {
                      setValue(q);
                      sendChatMessage({ newMessage: q });
                    },
                  })}
                </m.div>
              )}
              <div
                className={cn(
                  'caddietw-flex caddietw-flex-col caddietw-h-full caddietw-w-full caddietw-px-3 caddietw-box-border md:caddietw-px-0 md:caddietw-border-none caddietw-bg-transparent caddietw-relative'
                )}
              >
                <div
                  style={{
                    boxShadow:
                      systemMessageState === SystemMessageState.ASSISTANT
                        ? theme.components.inputForm.boxShadow
                        : 'none',
                  }}
                  className={cn(
                    'superbar-main-wrapper',
                    'caddietw-flex',
                    'caddietw-flex-col',
                    'caddietw-h-full',
                    'caddietw-w-full',
                    'caddietw-rounded-[42px]'
                  )}
                >
                  <SystemMessage
                    isCompactView={isCompactView}
                    shouldShowAIFilters={
                      shouldShowAIFilters ||
                      shouldShowAiFiltersForPage ||
                      aiFiltersEnabled
                    }
                    session={currentSession}
                    handleSelectFilter={searchWithFilters}
                    isTtsMuted={
                      voiceModeState !== 'off' ? isTtsMuted : undefined
                    }
                    toggleTtsMute={
                      voiceModeState !== 'off' ? toggleTtsMute : undefined
                    }
                  />

                  <SuperbarInput
                    value={value}
                    setValue={setValue}
                    loading={loading}
                    sendChatMessage={sendChatMessage}
                    store={store}
                    storeSettings={storeSettings}
                    isCompactView={isCompactView}
                    setIsCompactView={setIsCompactView}
                    handleClearAllAIFilters={handleClearAllAIFilters}
                    voice={{
                      voiceModeState,
                      toggleVoiceMode,
                      exitVoiceMode,
                      isVoiceModeAvailable,
                      analyserService: effectiveAnalyserService,
                      isListening: stt.isListening,
                      transcript: stt.transcript,
                      isVoiceSupported: stt.isSupported,
                      micPermissionDenied: stt.permissionDenied,
                      speechServiceBlocked: stt.serviceBlocked,
                      startListening: stt.startListening,
                      stopListening: stt.stopListening,
                    }}
                  />
                </div>
              </div>
            </m.div>
          ) : superBarViewState === SuperBarViewState.CLOSED ? (
            <m.div
              id="chat-view-closed"
              className={cn(
                'caddie-style-root-closed caddietw-w-auto caddietw-fixed caddietw-bottom-0 caddietw-transform caddietw--translate-x-1/2 caddietw-z-[99999] caddietw-pointer-events-auto caddietw-overflow-visible',
                fullScreenTakeover && 'caddietw-hidden'
              )}
              onClick={(e) => e.stopPropagation()}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{
                duration: 0.2,
                ease: 'easeInOut',
              }}
              key="chat-view-closed"
            >
              {/* Soft glow behind the closed bar */}
              <div
                className={cn(
                  'caddietw-flex caddietw-shadow-[0_0_20px_0_rgba(0,0,0,0.2)] caddietw-rounded-t-[20px] caddietw-px-4 caddietw-relative caddietw-z-10'
                )}
                style={{
                  backgroundColor: theme.components.chatShellClosed.background,
                  boxShadow: undefined,
                }}
              >
                <SuperbarMenu onClear={handleClearAllAIFilters} />
              </div>
            </m.div>
          ) : null}
        </AnimatePresence>
      </LazyMotion>
      <ProductGallery
        storeConfig={storeConfig}
        currentPage={currentPage}
        totalPages={searchResults?.searchPagination?.totalPages}
        loading={isGalleryLoading}
        onPageChange={(page) => {
          moveToPage(page);
        }}
        products={searchResults?.products || []}
      />
    </>
  );
};

export const ChatView = ({
  isSuperserveUi,
  isPlayground,
  feedbackControl,
  suggestedQuestionsView,
  onPlaygroundProgress,
  onPlaygroundCtaClick,
}: {
  isSuperserveUi?: boolean;
  isPlayground?: boolean;
  feedbackControl?: (props: FeedbackControlProps) => ReactNode;
  suggestedQuestionsView?: (props: SuggestedQuestionsViewProps) => ReactNode;
  onPlaygroundProgress?: () => void;
  onPlaygroundCtaClick?: (url: string) => void;
}) => {
  useEffect(() => {
    if (!getEnv('GROWTHBOOK_CLIENT_KEY')) return;
    const hasLocalFeatures = Object.keys(localFeatures).length > 0;
    growthbook.init({
      timeout: 2000,
      ...(hasLocalFeatures && { payload: { features: localFeatures } }),
    });
  }, []);

  return (
    <Providers>
      <GrowthBookProvider growthbook={growthbook}>
        <ChatContextProvider
          isSuperserveUi={isSuperserveUi ?? false}
          isPlayground={isPlayground ?? false}
          onPlaygroundCtaClick={onPlaygroundCtaClick}
        >
          <ChatViewComponent
            feedbackControl={feedbackControl}
            suggestedQuestionsView={suggestedQuestionsView}
            onPlaygroundProgress={onPlaygroundProgress}
          />
          <DevTools />
        </ChatContextProvider>
      </GrowthBookProvider>
    </Providers>
  );
};
